import { Form, Radio, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './OTPMethodSelectForm.module.scss';
import { getVerificationCode } from '../../requests/getVerificationCode';

const FREE_METHOD_IDS = [3, 4, 5];

const OTPMethodSelectNode = ({
  setStep,
  form,
  onMethodSelect,
  username,
  password,
  verificationTypes: verificationTypesFromProps,
  showAllMethods = false,
  disabledVerificationTypesById = {},
  supportContactNotice = null,
  onVerificationCodeResponse,
}) => {
  const { getFieldDecorator } = form;
  const [submittingMethodId, setSubmittingMethodId] = useState(null);
  const verificationTypesFromStore = useSelector(
    (state) => state.authentication?.twoFactorVerificationTypes
  );

  const hasVerificationTypesFromProps = Array.isArray(
    verificationTypesFromProps
  );

  const verificationTypes = hasVerificationTypesFromProps
    ? verificationTypesFromProps
    : verificationTypesFromStore;

  const hasVerificationTypes =
    Array.isArray(verificationTypes) && verificationTypes.length > 0;

  const effectiveVerificationTypes = useMemo(() => {
    if (!hasVerificationTypes) return [];

    if (hasVerificationTypesFromProps) {
      return verificationTypes;
    }

    if (showAllMethods) {
      return verificationTypes;
    }

    const freeMethods = verificationTypes.filter((type) =>
      FREE_METHOD_IDS.includes(Number(type?.id))
    );

    if (freeMethods.length === 0) {
      return verificationTypes;
    }

    const disabledTypes = verificationTypes.filter((type) =>
      Boolean(disabledVerificationTypesById[String(type?.id)])
    );

    const combined = [...freeMethods, ...disabledTypes];
    const uniqueById = new Map(combined.map((t) => [String(t?.id), t]));

    return Array.from(uniqueById.values());
  }, [
    hasVerificationTypes,
    hasVerificationTypesFromProps,
    showAllMethods,
    verificationTypes,
    disabledVerificationTypesById,
  ]);

  const handleMethodChange = async (e) => {
    const method = e?.target?.value;

    if (!method || submittingMethodId) return;

    const selected = effectiveVerificationTypes.find(
      (t) => String(t.id) === String(method)
    );
    const isDisabled = Boolean(disabledVerificationTypesById[String(method)]);

    if (!selected || isDisabled) return;

    setSubmittingMethodId(method);

    try {
      if (onMethodSelect) {
        onMethodSelect(selected);
      }

      const verificationCodeResponse = await getVerificationCode({
        authParameter: {
          login: username,
          password: password,
        },
        verificationTypeId: method,
      });

      if (!verificationCodeResponse) {
        setSubmittingMethodId(null);
        return;
      }

      if (typeof onVerificationCodeResponse === 'function') {
        const isHandled = await onVerificationCodeResponse(
          selected,
          verificationCodeResponse
        );

        if (isHandled) {
          return;
        }
      }

      setStep((prev) => prev + 1);
    } catch (error) {
      setSubmittingMethodId(null);
    }
  };

  if (!hasVerificationTypes) {
    return (
      <div className={styles.root}>
        <p className="login_page-main-title">Выбор метода верификации</p>
        <div className={styles.text}>
          Доступные методы верификации не найдены
        </div>
      </div>
    );
  }

  return (
    <Form className={styles.root}>
      <p className="login_page-main-title">Выберите способ подтверждения</p>
      <Form.Item className={styles.field}>
        {getFieldDecorator('method')(
          <Radio.Group
            className={styles.radioGroup}
            onChange={handleMethodChange}
          >
            {effectiveVerificationTypes.map((type) => {
              const value = String(type.id);
              const label = type.name;
              const isDisabled = Boolean(
                disabledVerificationTypesById[String(type?.id)]
              );
              const disabledTooltipText = disabledVerificationTypesById[value];

              return (
                <React.Fragment key={type.id}>
                  {isDisabled ? (
                    <Tooltip
                      title={disabledTooltipText}
                      placement="top"
                    >
                      <span>
                        <Radio.Button
                          value={value}
                          className={styles.radioButton}
                          disabled
                        >
                          {label}
                        </Radio.Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Radio.Button
                      value={value}
                      className={styles.radioButton}
                      disabled={Boolean(submittingMethodId)}
                    >
                      {label}
                    </Radio.Button>
                  )}
                </React.Fragment>
              );
            })}
          </Radio.Group>
        )}
      </Form.Item>
      {supportContactNotice && (
        <div className={styles.supportNotice}>{supportContactNotice}</div>
      )}
    </Form>
  );
};

export const OTPMethodSelectForm = Form.create({
  name: 'otp_method_select_form',
})(OTPMethodSelectNode);
